import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Alert, Container, TextField } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { verifyEmailApi, resendVerificationApi } from '../api/auth';

function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

const VerifyEmail = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState(query.get('email') || '');
  const token = query.get('token');

  useEffect(() => {
    const autoVerify = async () => {
      if (token && email) {
        setStatus('loading');
        try {
          const data = await verifyEmailApi({ token, email });
          if (data?.status === 'success') {
            setStatus('success');
            setMessage('Email verified successfully. You can log in after superadmin approval.');
          } else {
            setStatus('error');
            setMessage(data?.message || 'Verification failed');
          }
        } catch (e) {
          const apiMsg = e?.response?.data?.message;
          if (apiMsg && apiMsg.toLowerCase().includes('already verified')) {
            setStatus('success');
            setMessage('Email already verified. You can log in after superadmin approval.');
          } else {
            setStatus('error');
            setMessage(apiMsg || 'Verification failed');
          }
        }
      }
    };
    autoVerify();
  }, [token, email]);

  const resend = async () => {
    setStatus('loading');
    setMessage('');
    try {
      const data = await resendVerificationApi({ email });
      if (data?.status === 'success') {
        setStatus('success');
        setMessage('Verification email sent. Please check your inbox.');
      } else {
        setStatus('error');
        setMessage(data?.message || 'Failed to send verification email');
      }
    } catch (e) {
      setStatus('error');
      setMessage(e?.response?.data?.message || 'Failed to send verification email');
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'white' }}>
        <Container maxWidth="sm">
          <Box sx={{ bgcolor: 'white', borderRadius: 2, p: { xs: 3, sm: 5 } }}>
            <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>Verify your email</Typography>
            {message && (
              <Alert severity={status === 'success' ? 'success' : status === 'error' ? 'error' : 'info'} sx={{ mb: 2 }}>
                {message}
              </Alert>
            )}
            {status === 'success' ? (
              <Button variant="contained" onClick={() => navigate('/signin')} sx={{ bgcolor: '#00bcd4', '&:hover': { bgcolor: '#009bb3' } }}>
                Go to login
              </Button>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Enter your email to resend the verification link if needed.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <TextField fullWidth label="Email address" value={email} onChange={(e) => setEmail(e.target.value)} />
                </Box>
                <Button variant="contained" disabled={!email || status === 'loading'} onClick={resend} sx={{ bgcolor: '#00bcd4', '&:hover': { bgcolor: '#009bb3' } }}>
                  Resend verification email
                </Button>
              </>
            )}
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default VerifyEmail;


